import { useState, useEffect } from "react";
import {
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    CircularProgress,
    Typography,
    Box,
    Tooltip,
    InputAdornment,
    TableContainer,
    Fade,
    Dialog,
    DialogContent,
    Chip,
    IconButton,
    Drawer,
    useMediaQuery,
    useTheme
} from "@mui/material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { fetchData } from "../../../apiService";
import { useAuth } from "../../../AuthContext";
import { tokenIsExpired } from "../../../authUtils";
import EstimateModal from "../../pages/Estimate/EstimateModal";

function MonthlyEstimateReport() {
    const { isAuthenticated, companyId, branchId, token, logout, username } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [deliveryFilter, setDeliveryFilter] = useState("All"); // All, DeliveryOut, Pending
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Modal states
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedEstimate, setSelectedEstimate] = useState(null);
    const [fetchingEstimate, setFetchingEstimate] = useState(false);

    // Data for modal
    const [customers, setCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [enquiries, setEnquiries] = useState([]);
    const [companyDetails, setCompanyDetails] = useState(null);
    const [branchDetails, setBranchDetails] = useState(null);

    useEffect(() => {
        setMounted(true);
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }

        const today = new Date();
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        setFromDate(firstDayOfMonth.toISOString().split("T")[0]);
        setToDate(today.toISOString().split("T")[0]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (fromDate && toDate && companyId && branchId && token) {
            fetchTableData();
            fetchCustomers();
            fetchItems();
            fetchEnquiries();
            fetchCompanyDetails();
            fetchBranchDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromDate, toDate]);

    const fetchCompanyDetails = async () => {
        try {
            const response = await fetchData('CompanyApi', `/${companyId}`, token);
            console.log('Company API Response:', response);
            if (response && response.responseCode === '1111') {
                // Handle both single object and array responses
                const data = response.response_data || response.data || response;
                setCompanyDetails(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            console.error('Error fetching company details:', error);
        }
    };

    const fetchBranchDetails = async () => {
        try {
            const response = await fetchData('BranchApi', `/${branchId}`, token);
            console.log('Branch API Response:', response);
            if (response && response.responseCode === '1111') {
                // Handle both single object and array responses
                const data = response.response_data || response.data || response;
                setBranchDetails(Array.isArray(data) ? data[0] : data);
            }
        } catch (error) {
            console.error('Error fetching branch details:', error);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await fetchData('CustomerApi', `/${companyId}`, token);
            if (response && response.responseCode === '1111' && response.response_data) {
                setCustomers(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
        }
    };

    const fetchItems = async () => {
        try {
            const response = await fetchData('SaleApi', `/ItemStockListDetails/${companyId}/${branchId}`, token);
            if (response && response.responseCode === '1111' && response.response_data) {
                setItems(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching items:', error);
        }
    };

    const fetchEnquiries = async () => {
        try {
            const response = await fetchData('EnquiryApi', `/EnquiryList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response && response.responseCode === '1111' && response.response_data) {
                setEnquiries(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching enquiries:', error);
        }
    };

    const fetchSingleEstimate = async (estimateId) => {
        setFetchingEstimate(true);
        try {
            const response = await fetchData('EstimateApi', `/${estimateId}`, token);
            let estimateData = response.response_data || response;

            if (estimateData.Estimate && estimateData.EstimateDetails) {
                return {
                    ...estimateData.Estimate,
                    EstimateDetails: estimateData.EstimateDetails
                };
            }

            return estimateData;
        } catch (error) {
            console.error('Error fetching single Estimate:', error);
            return null;
        } finally {
            setFetchingEstimate(false);
        }
    };

    const handleViewEstimate = async (estimateId) => {
        const fullData = await fetchSingleEstimate(estimateId);
        if (fullData) {
            setSelectedEstimate(fullData);
            setModalOpen(true);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-GB");
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || amount === "") return "₹0.00";
        return `₹${Number(amount).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const fetchTableData = async () => {
        if (!fromDate || !toDate) {
            return;
        }
        setIsLoading(true);
        
        // Reset filters to show all data
        setDeliveryFilter("All");
        setSearchQuery("");
        
        try {
            const response = await fetchData(
                "ReportApi",
                `/EstimateList/${companyId}/${branchId}/${fromDate}/${toDate}`,
                token
            );

            if (response.responseCode === "1111") {
                console.log("Estimate data:", response.response_data);
                setDataItem(response.response_data || []);
            }
        } catch (error) {
            console.error("Error fetching estimates:", error);
            setDataItem([]);
        } finally {
            setIsLoading(false);
        }
    };

    const filteredData = dataItem
        .filter((item) => {
            // Apply delivery status filter
            if (deliveryFilter === "DeliveryOut") {
                return item.IsConvertToDeliveryOut === true || item.IsConvertToDeliveryOut === "true";
            } else if (deliveryFilter === "Pending") {
                return item.IsConvertToDeliveryOut === false || item.IsConvertToDeliveryOut === "false" || !item.IsConvertToDeliveryOut;
            }
            return true; // "All" - show everything
        })
        .filter((item) =>
            Object.values(item).some((value) =>
                String(value).toLowerCase().includes(searchQuery.toLowerCase())
            )
        );

    const processedData = filteredData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleExportExcel = async () => {
        if (filteredData.length === 0) {
            alert("No data to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Monthly Estimates");

        // Add Report Title
        const titleRow = worksheet.addRow(['MONTHLY ESTIMATE REPORT']);
        titleRow.font = { bold: true, size: 16, color: { argb: "FF667eea" } };
        titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
        titleRow.height = 30;
        worksheet.mergeCells(1, 1, 1, 12); // Merge cells for title
        
        worksheet.addRow([]); // Empty row for spacing

        // Get company and branch names
        const companyName = companyDetails?.CompanyName || companyDetails?.Name || companyDetails?.CompanyName || 'N/A';
        const branchName = branchDetails?.BranchName || branchDetails?.Name || branchDetails?.BranchName || 'N/A';

        // Add Company and Branch on same line
        const companyBranchRow = worksheet.addRow(['Company:', companyName, '|', 'Branch:', branchName]);
        companyBranchRow.font = { bold: true, size: 12 };
        companyBranchRow.getCell(1).font = { bold: true };
        companyBranchRow.getCell(4).font = { bold: true };

        // Add Date Range, Generated By, and Generated Time on same line
        const currentDateTime = new Date().toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
        const infoRow = worksheet.addRow([
            'Date Range:', 
            `${formatDate(fromDate)} to ${formatDate(toDate)}`,
            '|',
            'Generated By:',
            username || 'N/A',
            '|',
            'Generated Time:',
            currentDateTime
        ]);
        infoRow.font = { bold: true, size: 12 };
        infoRow.getCell(1).font = { bold: true };
        infoRow.getCell(4).font = { bold: true };
        infoRow.getCell(7).font = { bold: true };

        // Add empty row
        worksheet.addRow([]);

        // Add Header Row
        const headerRow = worksheet.addRow([
            "Estimate No",
            "Bill Date",
            "Customer",
            "Contact",
            "Enquiry",
            "Program Date",
            "Delivery Date",
            "Return Date",
            "Total Amount",
            "Advance Amount",
            "Transport Charge",
            "Status"
        ]);

        headerRow.font = { bold: true };
        headerRow.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF667eea" }
        };
        headerRow.font = { color: { argb: "FFFFFFFF" }, bold: true };

        // Add Data Rows
        filteredData.forEach((row) => {
            const status = (row.IsConvertToDeliveryOut === true || row.IsConvertToDeliveryOut === "true") 
                ? "Delivery Out" 
                : "Pending";
            
            worksheet.addRow([
                row.EstimateNo,
                formatDate(row.BillDate),
                row.CustomerName,
                row.ContactNumber1,
                row.EnquiryNo,
                formatDate(row.ProgramDate),
                formatDate(row.DeliveryDate),
                formatDate(row.DeliveryReturnDate),
                row.TotalAmount,
                row.AdvanceAmount,
                row.TransportingCharge,
                status
            ]);
        });

        // Set column widths
        worksheet.columns.forEach((col) => {
            col.width = 20;
        });

        // Add Total Row
        const totalRow = worksheet.addRow([
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            "Total:",
            filteredData.reduce((a, b) => a + (b.TotalAmount || 0), 0),
            filteredData.reduce((a, b) => a + (b.AdvanceAmount || 0), 0),
            filteredData.reduce((a, b) => a + (b.TransportingCharge || 0), 0),
            ""
        ]);
        totalRow.font = { bold: true };

        const buffer = await workbook.xlsx.writeBuffer();
        const fileName = `Monthly_Estimates_${fromDate}_to_${toDate}.xlsx`;
        saveAs(new Blob([buffer]), fileName);
    };

    if (!mounted) return null;

    return (
        <Fade in={mounted} timeout={800}>
            <div style={{
                minWidth: '100%',
                background: '#f5f7fa',
                padding: '10px',
                position: 'relative'
            }}>
                <style>{`
                    @keyframes slideIn {
                        from { opacity: 0; transform: translateY(20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                    .action-button {
                        text-transform: none;
                        font-weight: 600;
                        letter-spacing: 0.5px;
                    }
                `}</style>

                <div className="suite-page-panel" style={{
                    width: '100%',
                    margin: '0 auto',
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    padding: '12px',
                    position: 'relative',
                    color: 'black',
                    fontFamily: "Segoe UI, sans-serif",
                }}>
                    {/* Header Section with Title */}
                    <div className="suite-section-heading" style={{
                        marginBottom: '12px',
                        paddingBottom: '8px',
                        borderBottom: '1px solid #f0f0f0'
                    }}>
                        <h4 style={{
                            fontSize: '17px',
                            fontWeight: '700',
                            margin: 0,
                            letterSpacing: '-0.3px',
                            fontFamily: "Segoe UI, sans-serif",
                            textTransform: 'uppercase',
                            color: 'black'
                        }}>
                            Monthly Estimate Report
                        </h4>
                    </div>

                    {/* Filters and Buttons Row */}
                    {!isMobile ? (
                        // Desktop View
                        <div style={{ 
                            marginBottom: '12px', 
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                gap: '8px', 
                                flexWrap: 'nowrap',
                                minWidth: 'fit-content',
                                alignItems: 'flex-end'
                            }}>
                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>From Date</div>
                                    <TextField
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        size="small"
                                        sx={{
                                            width: '140px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '11px',
                                                height: '28px',
                                                '&:hover fieldset': {
                                                    borderColor: "#0d766e",
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: "#0d766e",
                                                },
                                            },
                                        }}
                                    />
                                </div>

                                <div style={{ flexShrink: 0 }}>
                                    <div style={{ fontSize: '10px', color: '#666', marginBottom: '2px', paddingLeft: '4px' }}>To Date</div>
                                    <TextField
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        size="small"
                                        sx={{
                                            width: '140px',
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '6px',
                                                backgroundColor: '#f8f9fa',
                                                fontSize: '11px',
                                                height: '28px',
                                                '&:hover fieldset': {
                                                    borderColor: "#0d766e",
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: "#0d766e",
                                                },
                                            },
                                        }}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                    <Button
                                        variant={deliveryFilter === "All" ? "contained" : "outlined"}
                                        onClick={() => setDeliveryFilter("All")}
                                        style={{
                                            background: deliveryFilter === "All" ? "linear-gradient(135deg, #0d766e 0%, #164e63 100%)" : 'transparent',
                                            color: deliveryFilter === "All" ? 'white' : "#0d766e",
                                            borderColor: "#0d766e",
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            letterSpacing: '0.3px',
                                            whiteSpace: 'nowrap',
                                            minWidth: '60px'
                                        }}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        variant={deliveryFilter === "DeliveryOut" ? "contained" : "outlined"}
                                        onClick={() => setDeliveryFilter("DeliveryOut")}
                                        style={{
                                            background: deliveryFilter === "DeliveryOut" ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent',
                                            color: deliveryFilter === "DeliveryOut" ? 'white' : '#10b981',
                                            borderColor: '#10b981',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            letterSpacing: '0.3px',
                                            whiteSpace: 'nowrap',
                                            minWidth: '100px'
                                        }}
                                    >
                                        Delivery Out
                                    </Button>
                                    <Button
                                        variant={deliveryFilter === "Pending" ? "contained" : "outlined"}
                                        onClick={() => setDeliveryFilter("Pending")}
                                        style={{
                                            background: deliveryFilter === "Pending" ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'transparent',
                                            color: deliveryFilter === "Pending" ? 'white' : '#f59e0b',
                                            borderColor: '#f59e0b',
                                            padding: '4px 12px',
                                            borderRadius: '6px',
                                            fontSize: '11px',
                                            fontWeight: '600',
                                            height: '28px',
                                            textTransform: 'none',
                                            letterSpacing: '0.3px',
                                            whiteSpace: 'nowrap',
                                            minWidth: '80px'
                                        }}
                                    >
                                        Pending
                                    </Button>
                                </div>
                            
                                <TextField
                                    placeholder="Search estimates..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                    sx={{
                                        flexGrow: 1,
                                        minWidth: '200px',
                                        flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '28px',
                                            '&:hover fieldset': {
                                                borderColor: "#0d766e",
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: "#0d766e",
                                            },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon style={{ color: "#0d766e", fontSize: '14px' }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <Button
                                    variant="contained"
                                    onClick={fetchTableData}
                                    disabled={isLoading}
                                    startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        background: 'linear-gradient(135deg, #11998e 0%, #092a15ff 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        boxShadow: '0 2px 6px rgba(17, 153, 142, 0.3)',
                                        border: 'none',
                                        height: '28px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                    }}
                                >
                                    {isLoading ? "Loading..." : "Refresh"}
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={handleExportExcel}
                                    disabled={filteredData.length === 0}
                                    startIcon={<FileDownloadIcon style={{ fontSize: '14px' }} />}
                                    style={{
                                        background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '600',
                                        boxShadow: "0 2px 6px rgba(13, 118, 110, 0.3)",
                                        border: 'none',
                                        height: '28px',
                                        textTransform: 'none',
                                        letterSpacing: '0.3px',
                                        whiteSpace: 'nowrap',
                                        flexShrink: 0
                                    }}
                                >
                                    Export Excel
                                </Button>
                            </div>
                        </div>
                    ) : (
                        // Mobile View
                        <div style={{ 
                            marginBottom: '12px', 
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                gap: '8px', 
                                alignItems: 'center',
                                minWidth: 'fit-content'
                            }}>
                                <TextField
                                    type="date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    size="small"
                                    sx={{
                                        width: '130px',
                                        flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                        },
                                    }}
                                />
                                <TextField
                                    type="date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    size="small"
                                    sx={{
                                        width: '130px',
                                        flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            backgroundColor: '#f8f9fa',
                                            fontSize: '11px',
                                            height: '32px',
                                        },
                                    }}
                                />

                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchTableData}
                                        disabled={isLoading}
                                        sx={{
                                            background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                            color: 'white',
                                            width: '36px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            flexShrink: 0
                                        }}
                                    >
                                        {isLoading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Export">
                                    <IconButton
                                        onClick={handleExportExcel}
                                        disabled={filteredData.length === 0}
                                        sx={{
                                            background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)",
                                            color: 'white',
                                            width: '36px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            flexShrink: 0
                                        }}
                                    >
                                        <FileDownloadIcon />
                                    </IconButton>
                                </Tooltip>
                                
                                <Tooltip title="Filter & Search">
                                    <IconButton
                                        onClick={() => setFilterDrawerOpen(true)}
                                        sx={{
                                            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                            color: 'white',
                                            width: '36px',
                                            height: '32px',
                                            borderRadius: '6px',
                                            flexShrink: 0
                                        }}
                                    >
                                        <FilterListIcon />
                                    </IconButton>
                                </Tooltip>
                            </div>
                        </div>
                    )}

                    {/* Mobile Filter Drawer */}
                    <Drawer
                        anchor="right"
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                    >
                        <div style={{ width: '280px', padding: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <Typography variant="h6" style={{ fontWeight: '600' }}>Filters</Typography>
                                <IconButton onClick={() => setFilterDrawerOpen(false)} size="small">
                                    <CloseIcon />
                                </IconButton>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <Typography variant="subtitle2" style={{ marginBottom: '10px', fontWeight: '600' }}>Status</Typography>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <Button
                                        fullWidth
                                        variant={deliveryFilter === "All" ? "contained" : "outlined"}
                                        onClick={() => setDeliveryFilter("All")}
                                        style={{
                                            background: deliveryFilter === "All" ? "linear-gradient(135deg, #0d766e 0%, #164e63 100%)" : 'transparent',
                                            color: deliveryFilter === "All" ? 'white' : "#0d766e",
                                            borderColor: "#0d766e",
                                            textTransform: 'none',
                                        }}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant={deliveryFilter === "DeliveryOut" ? "contained" : "outlined"}
                                        onClick={() => setDeliveryFilter("DeliveryOut")}
                                        style={{
                                            background: deliveryFilter === "DeliveryOut" ? 'linear-gradient(135deg, #10b981, #059669)' : 'transparent',
                                            color: deliveryFilter === "DeliveryOut" ? 'white' : '#10b981',
                                            borderColor: '#10b981',
                                            textTransform: 'none',
                                        }}
                                    >
                                        Delivery Out
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant={deliveryFilter === "Pending" ? "contained" : "outlined"}
                                        onClick={() => setDeliveryFilter("Pending")}
                                        style={{
                                            background: deliveryFilter === "Pending" ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'transparent',
                                            color: deliveryFilter === "Pending" ? 'white' : '#f59e0b',
                                            borderColor: '#f59e0b',
                                            textTransform: 'none',
                                        }}
                                    >
                                        Pending
                                    </Button>
                                </div>
                            </div>

                            <div>
                                <Typography variant="subtitle2" style={{ marginBottom: '10px', fontWeight: '600' }}>Search</Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Search estimates..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon style={{ color: "#0d766e" }} />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </div>
                        </div>
                    </Drawer>

                    {/* Table */}                    {/* Table */}
                    <div style={{ marginBottom: '10px' }}>
                        <TableContainer component={Paper} style={{
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                            borderRadius: '10px',
                            overflow: 'hidden'
                        }}>
                            <Table sx={{ minWidth: 650 }} aria-label="estimate table">
                                <TableHead>
                                    <TableRow style={{
                                        background: "linear-gradient(135deg, #0d766e 0%, #164e63 100%)"
                                    }}>
                                        {["Estimate #", "Customer", "Date", "Amount", "Status", ""].map((head) => (
                                            <TableCell key={head} style={{
                                                color: 'white',
                                                fontWeight: '600',
                                                fontSize: '11px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                padding: '4px 8px',
                                                textAlign: 'left',
                                                height : '10px'
                                            }}>
                                                {head}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                                <CircularProgress size={40} sx={{ color: "#0d766e" }} />
                                                <Typography sx={{ mt: 2, color: '#64748b' }}>
                                                    Loading estimates...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : processedData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                                <Typography color="text.secondary" style={{ fontSize: '12px' }}>
                                                    No estimates found for the selected date range
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        processedData.map((row, index) => (
                                            <TableRow key={row.Id} style={{
                                                background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                                                borderBottom: '2px solid #f0f0f0'
                                            }}>
                                                <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                                                    <div style={{ fontWeight: '600', color: "#0d766e" }}>
                                                        {row.EstimateNo}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                        {formatDate(row.BillDate)}
                                                    </div>
                                                    <div style={{ fontWeight: '600', color: "#0d766e" }}>
                                                        EQ NO : {row.EnquiryNo || "N/A"}
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                                                    <div style={{ fontWeight: '600' }}>
                                                        {row.CustomerName}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                        {row.ContactNumber1}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                        Event : {row.EventName}
                                                    </div>
                                                </TableCell>



                                                <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                                                    <div style={{ fontWeight: '600' }}>
                                                        Program Date : {formatDate(row.ProgramDate)}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                        Delivery Date : {formatDate(row.DeliveryDate)}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                                        Return Date : {formatDate(row.DeliveryReturnDate)}
                                                    </div>
                                                </TableCell>

                                                <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                                                    <div style={{ fontSize: '11px', color: '#062564ff' }}>
                                                        Total Amount : {formatCurrency(row.TotalAmount)}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#7f0579ff' }}>
                                                        Advance Amount : {formatCurrency(row.AdvanceAmount)}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#097979ff' }}>
                                                        Transporting Charge : {formatCurrency(row.TransportingCharge)}
                                                    </div>
                                                </TableCell>
                                                <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#ffffff', fontWeight: '600' }}>
                                                    {row.IsConvertToDeliveryOut === true || row.IsConvertToDeliveryOut === "true" ? (
                                                        <Chip
                                                            icon={<CheckCircleIcon style={{ color: '#ffffff', fontSize: '14px' }} />}
                                                            label="SUCCESSFULLY DELIVERY OUT"
                                                            sx={{
                                                                background: 'linear-gradient(145deg, #10b981, #059669)',
                                                                color: '#ffffff',
                                                                fontWeight: '700',
                                                                fontSize: '9px',
                                                                height: '28px',
                                                                borderRadius: '20px',
                                                                boxShadow: '0 4px 8px rgba(16, 185, 129, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.3)',
                                                                letterSpacing: '0.5px',
                                                                '& .MuiChip-label': {
                                                                    padding: '0 12px',
                                                                    color: '#ffffff'
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<HourglassEmptyIcon style={{ color: '#ffffff', fontSize: '14px' }} />}
                                                            label="WAITING FOR DELIVERY OUT"
                                                            sx={{
                                                                background: 'linear-gradient(145deg, #f59e0b, #d97706)',
                                                                color: '#ffffff',
                                                                fontWeight: '700',
                                                                fontSize: '9px',
                                                                height: '28px',
                                                                borderRadius: '20px',
                                                                boxShadow: '0 4px 8px rgba(245, 158, 11, 0.3), inset 0 -2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.3)',
                                                                letterSpacing: '0.5px',
                                                                '& .MuiChip-label': {
                                                                    padding: '0 12px',
                                                                    color: '#ffffff'
                                                                }
                                                            }}
                                                            
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                        <Tooltip title="View Details" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewEstimate(row.Id)}
                                                                sx={{
                                                                    width: '27px',
                                                                    height: '27px',
                                                                    borderRadius: '8px',
                                                                    background: "linear-gradient(145deg, #0d766e, #164e63)",
                                                                    color: 'white',
                                                                    boxShadow: "0 4px 8px rgba(13, 118, 110, 0.4), inset 0 -2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.3)",
                                                                    transition: 'all 0.2s ease',
                                                                    '&:hover': {
                                                                        background: "linear-gradient(145deg, #164e63, #0d766e)",
                                                                        transform: 'translateY(-2px)',
                                                                        boxShadow: "0 6px 12px rgba(13, 118, 110, 0.5), inset 0 -2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.3)"
                                                                    },
                                                                    '&:active': {
                                                                        transform: 'translateY(0)',
                                                                        boxShadow: "0 2px 4px rgba(13, 118, 110, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.3)"
                                                                    }
                                                                }}
                                                            >
                                                                <VisibilityIcon sx={{ fontSize: '16px' }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                    {/* Pagination */}
                    <TablePagination
                        component="div"
                        count={filteredData.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        sx={{
                            '& .MuiTablePagination-toolbar': {
                                fontSize: '12px'
                            },
                            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                fontSize: '12px',
                                fontWeight: 600
                            }
                        }}
                    />
                </div>

                {/* Loading Dialog */}
                <Dialog open={fetchingEstimate}>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
                        <CircularProgress size={48} sx={{ color: "#0d766e" }} />
                        <Typography>Loading estimate details...</Typography>
                    </DialogContent>
                </Dialog>

                {/* Estimate Modal */}
                <EstimateModal
                    open={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedEstimate(null);
                    }}
                    onSave={() => {
                        fetchTableData();
                        setModalOpen(false);
                        setSelectedEstimate(null);
                    }}
                    estimateData={selectedEstimate}
                    mode="view"
                    customers={customers}
                    items={items}
                    enquiries={enquiries}
                />
            </div>
        </Fade>
    );
}

export default MonthlyEstimateReport;
